import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchPublishedOrgs,
  filterOrgs,
  deriveOrgFacets,
  EMPTY_ORG_FILTERS,
  ORG_TYPES,
} from '../lib/organizations.js';
import { useAuth } from '../context/AuthContext.jsx';
import { loadWithRetry } from '../lib/loadWithRetry.js';
import OrgCard from '../components/organizations/OrgCard.jsx';
import OrgFilters from '../components/organizations/OrgFilters.jsx';
import OrgDetailModal from '../components/organizations/OrgDetailModal.jsx';

export default function Organizations() {
  const { t } = useTranslation();
  const { isActive } = useAuth();
  const [orgs, setOrgs] = useState(null);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [query, setQuery] = useState('');
  const [searchParams] = useSearchParams();
  // /organizations?type=journal preselects a type tab (the newsletter links
  // its Journals section here).
  const [filters, setFilters] = useState(() => {
    const type = searchParams.get('type');
    return ORG_TYPES.includes(type) ? { ...EMPTY_ORG_FILTERS, type } : EMPTY_ORG_FILTERS;
  });
  const [detail, setDetail] = useState(null);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setOrgs(null);
    setError(null);
    loadWithRetry(fetchPublishedOrgs, { label: 'organizations' })
      .then((rows) => !cancelled && setOrgs(rows))
      .catch((err) => !cancelled && setError(err.message ?? String(err)));
    return () => {
      cancelled = true;
    };
  }, [retryKey]);

  // Deep link from a What's New notice: /organizations?entry=<id>.
  useEffect(() => {
    const id = searchParams.get('entry');
    if (!id || !orgs) return;
    const match = orgs.find((o) => o.id === id);
    if (match) setDetail(match);
  }, [orgs, searchParams]);

  const facets = useMemo(() => (orgs ? deriveOrgFacets(orgs) : null), [orgs]);
  const results = useMemo(() => {
    if (!orgs) return [];
    return filterOrgs(orgs, { ...filters, query });
  }, [orgs, filters, query]);

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{error}</p>
        <button
          type="button"
          className="btn-primary mt-6"
          onClick={() => setRetryKey((k) => k + 1)}
        >
          {t('common.tryAgain')}
        </button>
      </div>
    );
  }
  if (!orgs || !facets) {
    return (
      <div role="status" aria-live="polite" className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center text-ink-secondary">
        {t('common.loading')}
      </div>
    );
  }

  const activeCount = (filters.country ? 1 : 0);

  return (
    <div className="mx-auto max-w-shell px-6 pb-20 pt-28">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="section-label">{t('home.tiles.organizations.title')}</div>
          <h1 className="font-serif text-4xl font-semibold text-cream">
            {t('organizations.heading')}
          </h1>
          <p className="mt-2 max-w-2xl text-ink-secondary">
            {t('organizations.subheading', { count: orgs.length })}
          </p>
        </div>
        {isActive && (
          <Link to="/organizations/new" className="btn-primary">
            {t('organizations.add')}
          </Link>
        )}
      </header>

      {/* Type tabs */}
      <div className="mb-6 flex flex-wrap gap-2 border-b border-white/10 pb-3">
        <TypeTab
          active={filters.type === ''}
          label={t('organizations.types.all')}
          count={orgs.length}
          onClick={() => setFilters({ ...filters, type: '' })}
        />
        {ORG_TYPES.map((type) => {
          const facet = facets.types.find((x) => x.type === type);
          const count = facet?.count ?? 0;
          if (count === 0) return null;
          return (
            <TypeTab
              key={type}
              active={filters.type === type}
              label={t(`organizations.types.${type}`)}
              count={count}
              onClick={() => setFilters({ ...filters, type })}
            />
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-navy-base/50 p-3 backdrop-blur">
        <input
          type="search"
          placeholder={t('organizations.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="min-w-0 flex-1 rounded-md border border-white/10 bg-navy-deep/60 px-4 py-2 text-cream placeholder:text-ink-muted focus:border-gold/50"
        />
        <button
          type="button"
          className="btn-ghost text-sm md:hidden"
          onClick={() => setShowFiltersMobile((s) => !s)}
        >
          {t('common.filter')}
          {activeCount > 0 && (
            <span className="ml-1 rounded-full bg-gold/20 px-1.5 text-xs text-gold-light">{activeCount}</span>
          )}
        </button>
      </div>

      <p className="mb-4 text-sm text-ink-secondary">
        {t('organizations.resultCount', { count: results.length })}
      </p>

      <div className="grid gap-8 md:grid-cols-[260px_1fr]">
        <div className={`md:block ${showFiltersMobile ? 'block' : 'hidden'}`}>
          <OrgFilters
            facets={facets}
            filters={filters}
            onChange={setFilters}
            onClear={() => setFilters({ ...filters, country: '' })}
            activeCount={activeCount}
          />
        </div>

        <div>
          {results.length === 0 ? (
            <p className="rounded-lg border border-white/10 bg-navy-base/40 p-10 text-center text-ink-secondary">
              {orgs.length === 0 ? t('organizations.emptyAll') : t('organizations.empty')}
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {results.map((o) => (
                <OrgCard key={o.id} org={o} onOpen={setDetail} />
              ))}
            </div>
          )}
        </div>
      </div>

      <OrgDetailModal
        org={detail}
        onClose={() => setDetail(null)}
        onDeleted={(id) => setOrgs((prev) => prev?.filter((o) => o.id !== id) ?? prev)}
      />
    </div>
  );
}

function TypeTab({ active, label, count, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm transition ${
        active
          ? 'border-gold/60 bg-navy-mid/60 text-gold-light'
          : 'border-white/10 bg-navy-deep/40 text-ink-secondary hover:border-gold/30'
      }`}
    >
      {label}
      <span className="rounded-full bg-navy-deep/60 px-2 text-[0.7rem] text-ink-muted">{count}</span>
    </button>
  );
}
